import React from "react";
import PropTypes from "prop-types";
import {
    Typography,
    Box,
    Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import BusyOverlay from "./commonComponents/BusyOverlay";
import DataTable from "./data_table/DataTable";
import { optimizedCellClassName } from "./data_table/tableClasses";
import Skeleton from "./ui/Skeleton";
import KitTooltip from "./ui/Tooltip";
import { sectionTitleSx } from "../styles/typography";

// Portfolio metrics from the receipt. "percent" metrics change in percentage
// points; positive "usd" metrics carry the backend's relative change; signed
// metrics that can cross zero show their change in dollars.
const METRIC_ROWS = Object.freeze([
    {
        key: "total_alpha_score",
        label: "Trade-idea score ($)",
        kind: "usd",
        change: "absolute",
        tooltip: "Sum of position value × trade idea.",
    },
    { key: "port_vol_pct_aum", label: "Total volatility (% of AUM)", kind: "percent" },
    { key: "beta_exp", label: "Beta exposure ($)", kind: "usd", change: "absolute" },
    { key: "factor_risk", label: "Factor volatility ($)", kind: "usd" },
    {
        key: "factor_risk_pct_of_total",
        label: "Factor share of variance (%)",
        kind: "percent",
        tooltip: "Factor variance ÷ total variance. Variances add across factor and "
            + "idiosyncratic risk; volatilities do not.",
    },
    { key: "idio_risk", label: "Idiosyncratic volatility ($)", kind: "usd" },
    { key: "idio_risk_pct", label: "Idiosyncratic share of variance (%)", kind: "percent" },
    { key: "gmv", label: "GMV ($)", kind: "usd" },
]);

// A Max Sharpe receipt also reports its forecast. Expected return is the
// receipt's Sharpe ratio times its total volatility: position value × expected
// return, summed and divided by AUM.
const EXPECTED_RETURN_ROWS = Object.freeze([
    {
        key: "expected_return_pct_aum",
        label: "Expected return (% of AUM)",
        kind: "percent",
        tooltip: "Sum of position value × expected return, divided by AUM. Expected returns "
            + "come from your trade ideas, scaled by each name’s specific risk and your skill.",
    },
    {
        key: "sharpe_ratio",
        label: "Ex-ante Sharpe ratio",
        kind: "ratio",
        change: "difference",
        tooltip: "Expected return ÷ total volatility.",
    },
]);

const METRIC_BY_KEY = Object.fromEntries(
    [...EXPECTED_RETURN_ROWS, ...METRIC_ROWS].map((row) => [row.key, row]),
);

function withExpectedReturn(metrics) {
    if (metrics?.sharpe_ratio == null || metrics?.port_vol_pct_aum == null) return metrics;
    return {
        ...metrics,
        expected_return_pct_aum: Number(metrics.sharpe_ratio) * Number(metrics.port_vol_pct_aum),
    };
}

function formatMetricValue(value, kind) {
    if (value === undefined || value === null) return "";
    const number = Number(value);
    if (kind === "percent") return `${number.toFixed(2)}%`;
    if (kind === "ratio") return number.toFixed(2);
    return number.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function formatChange(value, kind) {
    if (value === undefined || value === null || !Number.isFinite(Number(value))) return "";
    const number = Number(value);
    const sign = number > 0 ? "+" : number < 0 ? "-" : "";
    const magnitude = Math.abs(number);
    if (kind === "percent") return `${sign}${magnitude.toFixed(2)} pp`;
    if (kind === "ratio") return `${sign}${magnitude.toFixed(2)}`;
    if (kind === "usd_absolute") {
        const compact = magnitude >= 1_000_000;
        return `${sign}$${magnitude.toLocaleString("en-US", {
            notation: compact ? "compact" : "standard",
            maximumFractionDigits: compact ? 2 : 0,
        })}`;
    }
    return `${sign}${magnitude.toFixed(2)}%`;
}

function formatCompactUsd(value) {
    return `$${Math.abs(Number(value)).toLocaleString("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
    })}`;
}

function ChangeValue({ value, kind }) {
    const number = Number(value);
    const glyph = number > 0 ? "▲" : number < 0 ? "▼" : null;
    return (
        <span className="inline-flex items-center gap-[3px]">
            {glyph && <span className="text-[0.62rem] text-content-secondary">{glyph}</span>}
            {formatChange(value, kind)}
        </span>
    );
}

ChangeValue.propTypes = {
    value: PropTypes.number,
    kind: PropTypes.string.isRequired,
};

function VarianceShareBars({ actual, optimized, baselineLabel }) {
    const rows = [
        { label: baselineLabel, metrics: actual },
        { label: "Optimized", metrics: optimized },
    ].filter(({ metrics }) => (
        metrics?.factor_risk_pct_of_total != null && metrics?.idio_risk_pct != null
        && Number.isFinite(Number(metrics.factor_risk_pct_of_total))
        && Number.isFinite(Number(metrics?.idio_risk_pct))
    ));
    if (!rows.length) return null;
    return (
        <Box sx={{ mt: 1.5 }}>
            <Tooltip
                title="Factor and idiosyncratic variance add to total variance; their volatilities do not."
                arrow
                placement="top-start"
            >
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    Share of total variance
                </Typography>
            </Tooltip>
            {rows.map(({ label, metrics }) => {
                const factorShare = Math.max(0, Math.min(100, Number(metrics.factor_risk_pct_of_total)));
                return (
                    <Box
                        key={label}
                        sx={{ display: "grid", gridTemplateColumns: "120px 1fr", alignItems: "center", gap: 1, mt: 0.6 }}
                    >
                        <Typography variant="caption" noWrap>{label}</Typography>
                        <Box
                            role="img"
                            aria-label={`${label}: factor ${factorShare.toFixed(1)}%, idiosyncratic ${(100 - factorShare).toFixed(1)}% of total variance`}
                            sx={{ display: "flex", height: 20, borderRadius: 1, overflow: "hidden", fontSize: "0.68rem", fontWeight: 700 }}
                        >
                            <Box
                                sx={{
                                    width: `${factorShare}%`,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                }}
                            >
                                {factorShare >= 12 ? `${factorShare.toFixed(1)}%` : ""}
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.3),
                                    color: "text.primary",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                }}
                            >
                                {100 - factorShare >= 12 ? `${(100 - factorShare).toFixed(1)}%` : ""}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
            <Box sx={{ display: "flex", gap: 2, mt: 0.75, ml: "128px" }}>
                {[
                    { label: "Factor", sx: { bgcolor: "primary.main" } },
                    { label: "Idiosyncratic", sx: { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.3) } },
                ].map((item) => (
                    <Box key={item.label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "2px", ...item.sx }} />
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

VarianceShareBars.propTypes = {
    actual: PropTypes.object,
    optimized: PropTypes.object,
    baselineLabel: PropTypes.string.isRequired,
};

// The metrics table is dense: 4px side padding, 0.72rem cells, 34px rows,
// header labels on up to two lines. Its columns share the panel 43/19/19/19
// and keep those shares down to a 300px table.
const METRICS_TABLE_CLASS_NAME = "![--tbl-cell-px:4px] [&_td]:text-[0.72rem] [&_td]:leading-[1.2]";
const STALE_CELL_STYLE = Object.freeze({ opacity: 0.56 });
const NMV_KEY = "nmv";
const getMetricRowId = (row) => row.key;

const RunOptimizerResultTable = ({
    riskMetricsActPort,
    riskMetricsOptPort,
    riskMetricsDeltaPort,
    showPortfolioMetrics,
    optimizerIsRunning,
    viewModeLoading,
    baselineLabel = "Baseline",
    constraintEvaluation = null,
    resultStale = false,
    excludedHoldings = null,
    hideTitle = false,
    alphaScoreTooltip = null,
    alphaScoreLabel = "Trade-idea score ($)",
    expectedReturnPolicy = null,
}) => {
    const actualPort = expectedReturnPolicy ? withExpectedReturn(riskMetricsActPort) : riskMetricsActPort;
    const optimizedPort = expectedReturnPolicy ? withExpectedReturn(riskMetricsOptPort) : riskMetricsOptPort;
    const deltaPort = actualPort?.expected_return_pct_aum != null && optimizedPort?.expected_return_pct_aum != null
        ? {
            ...riskMetricsDeltaPort,
            expected_return_pct_aum: optimizedPort.expected_return_pct_aum - actualPort.expected_return_pct_aum,
        }
        : riskMetricsDeltaPort;
    const metricRows = expectedReturnPolicy ? [...EXPECTED_RETURN_ROWS, ...METRIC_ROWS] : METRIC_ROWS;

    const getMetricValue = (key, type) => {
        if (!showPortfolioMetrics) return "";
        const portMaps = {
            actual: actualPort,
            optimal: optimizedPort,
            delta: deltaPort,
        };
        const raw = portMaps[type]?.[key];
        if (raw == null) return "";
        const kind = METRIC_BY_KEY[key]?.kind ?? "usd";
        return type === "delta" ? formatChange(raw, kind) : formatMetricValue(raw, kind);
    };

    // A relative change from a zero baseline is undefined, so it shows a dash.
    const metricChange = ({ key, kind, change }) => {
        if (!showPortfolioMetrics) return "";
        if (actualPort?.[key] == null || optimizedPort?.[key] == null) return "";
        const baseline = Number(actualPort?.[key]);
        const optimized = Number(optimizedPort?.[key]);
        if (change === "difference") {
            return Number.isFinite(baseline) && Number.isFinite(optimized)
                ? <ChangeValue value={optimized - baseline} kind={kind} />
                : "";
        }
        if (change === "absolute") {
            return Number.isFinite(baseline) && Number.isFinite(optimized)
                ? <ChangeValue value={optimized - baseline} kind="usd_absolute" />
                : "";
        }
        if (kind !== "percent" && baseline === 0) return "—";
        const delta = deltaPort?.[key];
        return delta == null ? "" : <ChangeValue value={delta} kind={kind} />;
    };

    const nmv = showPortfolioMetrics
        ? constraintEvaluation?.portfolio?.net_market_value_usd ?? null
        : null;
    const measuredRules = (constraintEvaluation?.rules ?? []).filter((rule) => (
        rule?.applied && rule.baseline_within_limit != null
    ));
    const baselineBreachCount = measuredRules.filter((rule) => (
        rule.baseline_within_limit === false
    )).length;
    const busy = optimizerIsRunning || viewModeLoading;
    const isInitialLoading = Boolean(!showPortfolioMetrics && busy);

    // One value column: a skeleton bar while the first result loads, else the value.
    const valueCell = (renderValue) => ({ row: { original } }) => (
        isInitialLoading ? <Skeleton width={60} className="ml-auto" /> : renderValue(original)
    );
    const staleStyle = () => (resultStale ? STALE_CELL_STYLE : undefined);
    const valueColumn = (id, header, renderValue, meta = {}) => ({
        id,
        header,
        enableSorting: false,
        minSize: 57,
        cell: valueCell(renderValue),
        meta: { width: "19%", align: "right", cellStyle: staleStyle, ...meta },
    });
    const columns = [
        {
            id: "metric",
            header: "Metric",
            enableSorting: false,
            minSize: 129,
            cell: ({ row: { original: { key, label, tooltip } } }) => {
                if (key === NMV_KEY) return label;
                let content = tooltip;
                if (key === "total_alpha_score" && alphaScoreTooltip) content = alphaScoreTooltip;
                else if (key === "expected_return_pct_aum") {
                    content = `${tooltip} Skill ${expectedReturnPolicy.information_coefficient}.`;
                }
                const text = key === "total_alpha_score" ? alphaScoreLabel : label;
                return content
                    ? <KitTooltip content={content} placement="top-start"><span>{text}</span></KitTooltip>
                    : text;
            },
            // Long labels wrap onto a second line, as they always did.
            meta: { width: "43%", wrap: true },
        },
        valueColumn("actual", baselineLabel, (row) => (
            row.key === NMV_KEY ? formatMetricValue(nmv?.baseline, "usd") : getMetricValue(row.key, "actual")
        )),
        valueColumn("optimal", "Optimized", (row) => (
            row.key === NMV_KEY ? formatMetricValue(nmv?.optimized, "usd") : getMetricValue(row.key, "optimal")
        ), { cellClassName: optimizedCellClassName, headerClassName: optimizedCellClassName }),
        valueColumn("delta", "Change", (row) => {
            if (row.key !== NMV_KEY) return metricChange(row);
            return nmv?.baseline != null && nmv?.optimized != null
                ? <ChangeValue value={nmv.optimized - nmv.baseline} kind="usd_absolute" />
                : "";
        }),
    ];
    const rows = [...metricRows, { key: NMV_KEY, label: "NMV ($)" }];

    return (
        <BusyOverlay show={busy && !isInitialLoading} minHeight={420}>
            <Box
                sx={{
                    width: "100%",
                    height: "auto",
                    display: "flex",
                    flexDirection: "column",
                    pointerEvents: showPortfolioMetrics ? "auto" : "none",
                }}
            >
                {!hideTitle && (
                    <Typography variant="subtitle1" sx={{ ...sectionTitleSx, pb: 1 }}>
                        Optimization Outcome
                    </Typography>
                )}

                {resultStale && showPortfolioMetrics && (
                    <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ mb: 0.5, fontWeight: 700 }}
                    >
                        From the previous configuration
                    </Typography>
                )}

                {showPortfolioMetrics && (baselineBreachCount > 0 || excludedHoldings) && (
                    <Box sx={{ display: "grid", gap: 0.25, mb: 0.75 }}>
                        {baselineBreachCount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {`${baselineLabel} breaks ${baselineBreachCount} of ${measuredRules.length} `
                                    + "active limits, so the optimizer had to trade to meet them."}
                            </Typography>
                        )}
                        {excludedHoldings && (
                            <Typography variant="caption" color="text.secondary">
                                {`Not in this run: ${excludedHoldings.count} held `
                                    + `name${excludedHoldings.count === 1 ? "" : "s"} `
                                    + `(${formatCompactUsd(excludedHoldings.gmv)}). `
                                    + `${excludedHoldings.count === 1 ? "It is" : "They are"} left out `
                                    + "of this result and any saved scenario."}
                            </Typography>
                        )}
                    </Box>
                )}

                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <DataTable
                        aria-label="Optimization outcome metrics"
                        data={rows}
                        columns={columns}
                        getRowId={getMetricRowId}
                        className={METRICS_TABLE_CLASS_NAME}
                        rowHeight={34}
                        wrapHeaders
                    />
                    {showPortfolioMetrics && (
                        <Box sx={{ opacity: resultStale ? 0.56 : 1 }}>
                            <VarianceShareBars
                                actual={riskMetricsActPort}
                                optimized={riskMetricsOptPort}
                                baselineLabel={baselineLabel}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </BusyOverlay>
    );
};

RunOptimizerResultTable.propTypes = {
    riskMetricsActPort: PropTypes.object,
    riskMetricsOptPort: PropTypes.object,
    riskMetricsDeltaPort: PropTypes.object,
    showPortfolioMetrics: PropTypes.bool,
    optimizerIsRunning: PropTypes.bool,
    viewModeLoading: PropTypes.bool,
    baselineLabel: PropTypes.string,
    constraintEvaluation: PropTypes.object,
    resultStale: PropTypes.bool,
    excludedHoldings: PropTypes.shape({
        count: PropTypes.number.isRequired,
        gmv: PropTypes.number.isRequired,
    }),
    hideTitle: PropTypes.bool,
    alphaScoreTooltip: PropTypes.string,
    alphaScoreLabel: PropTypes.string,
    expectedReturnPolicy: PropTypes.shape({
        name: PropTypes.string.isRequired,
        information_coefficient: PropTypes.number.isRequired,
    }),
};

export default RunOptimizerResultTable;
